import { type MailboxInfo } from 'owa-client-types';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type CreateAttachmentResponse from 'owa-service/lib/contract/CreateAttachmentResponse';
import type FileAttachment from 'owa-service/lib/contract/FileAttachment';
import type ItemId from 'owa-service/lib/contract/ItemId';
import createAttachmentJsonRequest from 'owa-service/lib/factory/createAttachmentJsonRequest';
import createAttachmentRequest from 'owa-service/lib/factory/createAttachmentRequest';
import fileAttachment from 'owa-service/lib/factory/fileAttachment';
import getMailboxRequestOptions from 'owa-service/lib/getMailboxRequestOptions';
import createAttachmentFromLocalFileOperation from 'owa-service/lib/operation/createAttachmentFromLocalFileOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

// This function is used to create a FileAttachment object from a Blob object
export async function getFileAttachment(
    content: Blob | string,
    contentType: string,
    isInline: boolean,
    filename?: string,
    size?: number,
    attachmentId?: string,
    contentId?: string,
    contentLocation?: string,
    attachmentOriginalUrl?: string
): Promise<FileAttachment> {
    const uninitializedAttachment: AttachmentType = {
        Name: undefined,
        ContentType: undefined,
        ContentId: undefined,
        ContentLocation: undefined,
        AttachmentOriginalUrl: undefined,
        Size: 0,
        LastModifiedTime: undefined,
        IsInline: false,
        IsInlineToUniqueBody: false,
        IsInlineToNormalBody: false,
        ThumbnailMimeType: undefined,
        Thumbnail: undefined,
    };

    let attachmentContent: string = '';
    let attachmentSize: number = size || 0;
    if (content instanceof Blob) {
        attachmentContent = await blobToBase64(content);
        attachmentSize = content.size;
    } else {
        attachmentContent = content;
    }

    return fileAttachment({
        ...uninitializedAttachment,
        Name: filename,
        Size: attachmentSize,
        Content: attachmentContent,
        ContentType: contentType,
        AttachmentId: attachmentId ? { Id: attachmentId } : undefined,
        IsInline: isInline,
        ContentId: contentId,
        ContentLocation: contentLocation,
        AttachmentOriginalUrl: attachmentOriginalUrl,
    });
}

// This function is used to send a request to attach a file to an item
export async function sendAttachRequest(
    attachments: AttachmentType[],
    parentItemId: ItemId,
    mailboxInfo: MailboxInfo
): Promise<CreateAttachmentResponse> {
    const attachmentTempId = new Date().toString();
    const requestBody = createAttachmentRequest({
        ParentItemId: parentItemId,
        Attachments: attachments,
        RequireImageType: false,
        IncludeContentIdInResponse: false,
        ClientSupportsIrm: false,
        CancellationId: attachmentTempId,
    });

    const jsonRequest = createAttachmentJsonRequest({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    });
    return createAttachmentFromLocalFileOperation(
        jsonRequest,
        getMailboxRequestOptions(mailboxInfo)
    ).then(response => {
        return response.Body;
    });
}

// This function is used to convert a Blob object to a base64 string
export async function blobToBase64(blob: Blob): Promise<string> {
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binaryString = '';

    for (const byte of bytes) {
        binaryString += String.fromCharCode(byte);
    }

    return btoa(binaryString);
}
